import React, { useCallback, useMemo } from "react";
import { Box, useTheme } from "arkh";

import { TeamHomePopoverState } from "../TeamHomePopoverState";
import { TeamPickerButton } from "../TeamPickerButton";
import { TeamHeaderDashboardButton } from "./TeamHeaderDashboardButton";
import { useNavigationHeaderHeight } from "../../../../../TopLevelNavigators/AHNavigationHeaderContainer";
import { graphql, useFragment } from "react-relay";
import { TeamHeader_team$key } from "./__generated__/TeamHeader_team.graphql";
import { UserLoggedInNavigatorNavigationProp } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { useSafeAreaInsets } from "react-native-safe-area-context";

import { TeamHeader_home$key } from "./__generated__/TeamHeader_home.graphql";
import { TeamHeaderSettingsButton } from "./TeamHeaderSettingsButton";
import { isDarkColor } from "../isDarkColor";
import { TeamHeader_store$key } from "./__generated__/TeamHeader_store.graphql";
import { usePersistedTeam } from "../../../../../OS/Login/State";
import { Portal } from "@gorhom/portal";
import { TeamSettingsPopover } from "../../../../TeamMorePopover/TeamSettingsPopover";
import { StorecastPickerPopover } from "../../../../StorecastPickerPopover/StorecastPickerPopover";
import { TeamPickerPopover } from "../TeamPickerPopover";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { TeamHeaderButton } from "./TeamHeaderButton";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { TeamHelpPopover } from "../../../../TeamMorePopover/TeamHelpPopover";
import {
  OrganisationHeader,
  OrganisationHeaderHeight,
} from "../../../../../components/OrgHeaders/OrganisationHeader";
import { Popover } from "../../../../../components/mobile/Popover";

import { TeamHeaderAssignedBadge } from "./TeamHeaderAssignedBadge";
import { TeamHeader_storeViewer$key } from "./__generated__/TeamHeader_storeViewer.graphql";
import { TeamHeaderProfileButton } from "../../../TeamHeader/TeamHeaderProfileButton";
import { TeamHeaderRoles } from "./TeamHeaderRoles";

export function TeamHeaderComponent(props: {
  team: TeamHeader_team$key;
  home: TeamHeader_home$key;
  store: TeamHeader_store$key;
  storeViewer: TeamHeader_storeViewer$key;
  navigation: UserLoggedInNavigatorNavigationProp<"Team">;
}) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const [popoverState, setPopoverState] =
    React.useState<TeamHomePopoverState>(null);

  const home = useFragment(
    graphql`
      fragment TeamHeader_home on HomeUnion {
        ... on Organisation {
          __typename
          id
          name
        }
      }
    `,
    props.home
  );

  const storeViewer = useFragment(
    graphql`
      fragment TeamHeader_storeViewer on StoreViewer {
        ...TeamHeaderAssignedBadge_storeViewer
        ...TeamPickerPopover_storeViewer
      }
    `,
    props.storeViewer
  );
  const store = useFragment(
    graphql`
      fragment TeamHeader_store on Store {
        ...TeamPickerPopover_store
        myRole
        name
        customizations
        ...TeamSettingsPopover_Store
        currentEmployee(id: $employeeId) {
          id
          ...TeamHeaderProfileButton_employee
        }
        me {
          ...TeamHeaderProfileButton_storeUser
        }
        dashboards {
          ...StorecastPickerPopoverDashboards
        }
      }
    `,
    props.store
  );

  const team = useFragment(
    graphql`
      fragment TeamHeader_team on Team {
        ...TeamPickerButton_team
        ...TeamHeaderRoles_team
        id
        name
        color
        canEdit
        ...TeamSettingsPopover_Team
      }
    `,
    props.team
  );

  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const headerHeight = useNavigationHeaderHeight();
  const hasAdmin = store.myRole === "ADMIN" || store.myRole === "MANAGER";

  const teamColor = team.color ?? theme.primary;
  const darkColor = isDarkColor(teamColor);
  const tintColor = darkColor ? "text-inverted" : "text-primary";

  const hasOrgButton = home.__typename === "Organisation";

  const hasHelp = store.customizations?.includes("DISABLE_HELP") ? false : true;
  const onClosePopover = useCallback(
    () => setPopoverState(null),
    [setPopoverState]
  );
  const onNavigateToStorecast = useCallback(
    (item: { id: string; title: string }) => {
      props.navigation.navigateDeprecated("Dashboard", {
        dashboardId: item.id,
      });
    },
    [props.navigation]
  );

  const popoverTop = useMemo(
    () =>
      headerHeight +
      insets.top +
      (home.__typename === "Organisation" ? OrganisationHeaderHeight : 0),
    [headerHeight, home.__typename, insets.top]
  );

  const persistedTeam = usePersistedTeam();
  const intl_tokens = useIntlTokens();

  const badge = useMemo(() => {
    return storeViewer ? (
      <TeamHeaderAssignedBadge storeViewer={storeViewer} />
    ) : null;
  }, [storeViewer]);

  return (
    <>
      <Box
        style={{
          backgroundColor: teamColor,
        }}
      >
        {hasOrgButton ? (
          <OrganisationHeader
            navigateDeprecatedToOrg={() =>
              props.navigation.navigateDeprecated("OrgHome", {})
            }
            storeName={store.name}
          />
        ) : null}
        <Box
          spaceHorizontal="small"
          insets={{ top: hasOrgButton ? 0 : insets.top }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            height: headerHeight + (hasOrgButton ? 0 : insets.top),
            backgroundColor: teamColor,
            justifyContent: "space-between",
          }}
        >
          <TeamPickerButton
            onPress={() => {
              setPopoverState("teamPicker");
            }}
            tintColor={tintColor}
            team={team}
            compact={isCompact}
          />
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              height: "100%",
            }}
          >
            <TeamHeaderDashboardButton
              tintColor={tintColor}
              navigation={props.navigation}
              openStorecastPicker={() => {
                setPopoverState("storecastPicker");
              }}
              compact={isCompact}
            />
            {hasAdmin ? (
              <TeamHeaderSettingsButton
                tintColor={tintColor}
                onPress={() => {
                  setPopoverState("teamMoreMenu");
                }}
                compact={isCompact}
              />
            ) : null}
            <TeamHeaderButton
              title={isCompact ? undefined : intl_tokens.Help}
              icon="help"
              spaceLeft={isCompact ? "small" : "medium"}
              spaceRight={isCompact ? "small" : "medium"}
              tintColor={tintColor}
              onPress={() => {
                setPopoverState("help");
              }}
              onPressIn={onPressInHaptic}
            />
            {home.__typename === "Organisation" ? null : (
              <TeamHeaderProfileButton
                user={store.me ?? null}
                employee={store.currentEmployee ?? null}
                onPress={() => {
                  props.navigation.navigateDeprecated("Profile", {});
                }}
                badge={badge}
                compact={isCompact}
              />
            )}
          </Box>
        </Box>
        <TeamHeaderRoles team={team} tintColor={tintColor} />
      </Box>
      <Portal hostName="team-popover-portal">
        {popoverState === "help" ? (
          <TeamHelpPopover
            onClose={onClosePopover}
            navigation={props.navigation}
            top={popoverTop}
            hasHelp={hasHelp}
          />
        ) : null}
        {popoverState === "teamMoreMenu" ? (
          <TeamSettingsPopover
            store={store}
            team={team}
            onClose={onClosePopover}
            top={popoverTop}
            navigation={props.navigation}
            showStoreAdmin={
              store.myRole === "ADMIN" || store.myRole === "MANAGER"
            }
            showTeamAdmin={team.canEdit}
          />
        ) : null}
        {popoverState === "storecastPicker" ? (
          <Popover onClose={onClosePopover} top={popoverTop} side="right">
            <StorecastPickerPopover
              onClose={onClosePopover}
              onSelectItem={onNavigateToStorecast}
              dashboards={store.dashboards}
            />
          </Popover>
        ) : null}
        {popoverState === "teamPicker" ? (
          <TeamPickerPopover
            top={popoverTop}
            selectedTeamId={team.id}
            storeViewer={storeViewer}
            store={store}
            onSelectTeamId={(teamId) => {
              persistedTeam.setCurrentTeamId(teamId);
              const navState = props.navigation.getState();
              const currentRoute = navState.routes[navState.index];
              const currentSub =
                currentRoute.state?.routes[currentRoute.state.index ?? 0];
              const screen =
                currentSub !== undefined &&
                ["TeamHome", "TeamFiles", "TeamPLU"].includes(currentSub.name)
                  ? { screen: currentSub.name }
                  : {};
              props.navigation.replace("Team", {
                teamId,
                ...screen,
              });
            }}
            onClose={onClosePopover}
          />
        ) : null}
      </Portal>
    </>
  );
}
